import { VgApiResult, vgApiRequest } from "./vg-api.service";

/**
 * Who is assigned to a booking configuration, and for how long.
 *
 * This is what makes a configuration bookable. Duration and price live on the
 * assignment rather than the configuration, which is the right way round: a
 * twenty minute consult with one vet and thirty with another is the same
 * consultation, priced differently. Until something is assigned there is nobody
 * to book and no length to book them for.
 */

/** Whether the resource does the work, or is something the work needs. */
export const RESOURCE_RELATIONSHIP = {
  UNKNOWN: 1,
  PERFORMS_SERVICE: 2,
  REQUIRED_BY_SERVICE: 3,
} as const;

export const RESOURCE_REQUIREMENT = {
  UNKNOWN: 1,
  OPTIONAL: 2,
  REQUIRED: 3,
} as const;

const MS_PER_MINUTE = 60 * 1000;

export type OrgResource = {
  resourceId: number;
  resourceName: string;
  /** The grid a booking sits on, in minutes. Zero when the resource has none. */
  slotMinutes: number;
};

export type ConfigAssignment = {
  serviceFulfilmentConfigResourceMapId: number;
  resourceId: number;
  durationMinutes: number;
  price: number;
  required: boolean;
  relationship: number;
};

export type AssignmentInput = {
  orgId: number;
  serviceId: number;
  serviceFulfilmentConfigId: number;
  resourceId: number;
  durationMinutes: number;
  price?: number;
  required?: boolean;
  relationship?: number;
};

function minutesToMs(minutes: number): number {
  return Math.round(minutes) * MS_PER_MINUTE;
}

function msToMinutes(milliseconds: unknown): number {
  const ms = Number(milliseconds ?? 0);
  return Number.isFinite(ms) ? Math.round(ms / MS_PER_MINUTE) : 0;
}

/**
 * The assignment as the platform wants it.
 *
 * Two things here are easy to get wrong and silent when they are. Duration is
 * stored in milliseconds while a clinic thinks in minutes, so sending minutes
 * books a twenty minute consult for twenty milliseconds. And these enums start
 * at 1, so a field left unset is not a sensible default - it is Unknown, which
 * validation refuses without naming the field.
 */
function toPayload(input: AssignmentInput) {
  return {
    orgId: input.orgId,
    serviceId: input.serviceId,
    serviceFulfilmentConfigId: input.serviceFulfilmentConfigId,
    resourceId: input.resourceId,
    durationMilliseconds: minutesToMs(input.durationMinutes),
    monetaryAmount: input.price ?? 0,
    serviceFulfilmentResourceConfigRelationship:
      input.relationship ?? RESOURCE_RELATIONSHIP.PERFORMS_SERVICE,
    serviceFulfilmentResourceConfigRequiredOptional:
      input.required === false
        ? RESOURCE_REQUIREMENT.OPTIONAL
        : RESOURCE_REQUIREMENT.REQUIRED,
  };
}

function tooShort(durationMinutes: number): boolean {
  return !Number.isFinite(durationMinutes) || durationMinutes <= 0;
}

const NO_DURATION_MESSAGE =
  "Give this person a length for the appointment - a booking with no duration ends when it starts.";

export async function assignResourceToConfig(
  accessToken: string,
  input: AssignmentInput
): Promise<VgApiResult<{ newRecordID?: number; NewRecordID?: number }>> {
  if (tooShort(input.durationMinutes)) {
    return { data: null, error: { message: NO_DURATION_MESSAGE } };
  }

  return vgApiRequest(
    accessToken,
    "Create_ServiceFulfilmentConfigResourceMap",
    toPayload(input)
  );
}

export async function updateConfigAssignment(
  accessToken: string,
  input: AssignmentInput & { serviceFulfilmentConfigResourceMapId: number }
): Promise<VgApiResult<unknown>> {
  if (tooShort(input.durationMinutes)) {
    return { data: null, error: { message: NO_DURATION_MESSAGE } };
  }

  // By the mapping's own id, not by resource and configuration: the same person
  // can be assigned to one configuration more than once, at different lengths.
  return vgApiRequest(accessToken, "Update_ServiceFulfilmentConfigResourceMap", {
    ...toPayload(input),
    serviceFulfilmentConfigResourceMapId:
      input.serviceFulfilmentConfigResourceMapId,
  });
}

export async function removeConfigAssignment(
  accessToken: string,
  orgId: number,
  serviceFulfilmentConfigResourceMapId: number
): Promise<VgApiResult<unknown>> {
  return vgApiRequest(accessToken, "Delete_ServiceFulfilmentConfigResourceMap", {
    orgId,
    serviceFulfilmentConfigResourceMapId,
  });
}

type AssignmentListResponse = {
  serviceFulfilmentConfigResourceMapList?: unknown[];
  ServiceFulfilmentConfigResourceMapList?: unknown[];
};

export async function readConfigAssignments(
  accessToken: string,
  orgId: number,
  serviceFulfilmentConfigId: number
): Promise<VgApiResult<ConfigAssignment[]>> {
  const { data, error } = await vgApiRequest<AssignmentListResponse>(
    accessToken,
    "Read_All_ServiceFulfilmentConfigResourceMaps_By_ServiceFulfilmentConfig_ID",
    { orgId, serviceFulfilmentConfigId }
  );

  if (error) return { data: null, error };

  const rows =
    data?.serviceFulfilmentConfigResourceMapList ??
    data?.ServiceFulfilmentConfigResourceMapList ??
    [];

  return {
    data: rows.map((raw) => {
      const row = raw as Record<string, unknown>;
      return {
        serviceFulfilmentConfigResourceMapId: Number(
          row.serviceFulfilmentConfigResourceMapId ?? 0
        ),
        resourceId: Number(row.resourceId ?? 0),
        durationMinutes: msToMinutes(row.durationMilliseconds),
        price: Number(row.monetaryAmount ?? 0),
        required:
          Number(row.serviceFulfilmentResourceConfigRequiredOptional ?? 0) !==
          RESOURCE_REQUIREMENT.OPTIONAL,
        relationship: Number(
          row.serviceFulfilmentResourceConfigRelationship ??
            RESOURCE_RELATIONSHIP.PERFORMS_SERVICE
        ),
      };
    }),
    error: null,
  };
}

type ResourceListResponse = {
  /** What Core's DCR_OrgResourcesCompleteList actually sends. */
  resourceCompleteList?: unknown[];
  resourceList?: unknown[];
  ResourceList?: unknown[];
};

/**
 * The people and things a clinic can assign - its vets, nurses, rooms.
 */
export async function readOrgResources(
  accessToken: string,
  orgId: number
): Promise<VgApiResult<OrgResource[]>> {
  const { data, error } = await vgApiRequest<ResourceListResponse>(
    accessToken,
    "Read_All_Organisation_Resources",
    { orgId }
  );

  if (error) return { data: null, error };

  const rows = data?.resourceCompleteList ?? data?.resourceList ?? data?.ResourceList ?? [];

  return {
    data: rows.map((raw) => {
      const row = raw as Record<string, unknown>;
      return {
        resourceId: Number(row.resourceId ?? 0),
        resourceName: String(row.resourceName ?? "").trim() || "Unnamed",
        slotMinutes: msToMinutes(row.slotDuration),
      };
    }),
    error: null,
  };
}
